import {
  Button,
  Card,
  Checkbox,
  Col,
  Input,
  InputNumber,
  message,
  Modal,
  Popover,
  Row,
  Select,
  Typography,
} from "antd";
import { RootState } from "app.store";
import { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  DefaultErrorMessage,
  Information,
  IsActive,
  Name,
} from "shared/constants/define-localization";
import { CoreResource, t } from "shared/plugins/localization";
import { getCountryList } from "states/address/address.api";
import { CountryModel, StateModel } from "states/address/address.models";
import { BranchModel } from "states/branch/branch.models";
import { WareHouseModel } from "states/ware-house/ware-house.models";
import { TimezoneModel } from "states/system-setting/system-setting.models";
import { getTimeZones } from "states/system-setting/system-setting.api";
import { SeoModel } from "shared/models/seo-model";
import { CopyOutlined } from "@ant-design/icons";
import { setLoading } from "states/global-value/global-value.slice";
import { copyMenuFromOtherBranch } from "states/branch/branch.api";

export interface IBranchInformationTabProps {
  model: BranchModel;
  onChange: (newModel: BranchModel) => void;
  isUpdateMode: boolean;
}

export function BranchInformationTab(props: IBranchInformationTabProps) {
  const { model, onChange, isUpdateMode } = props;
  const dispatch = useDispatch();
  const wareHouses = useSelector(
    (state: RootState) => state.wareHouse.wareHouseList,
    shallowEqual
  );
  const [countries, setCountries] = useState<CountryModel[]>([]);
  const [states, setStates] = useState<StateModel[]>([]);
  const [timeZones, setTimeZones] = useState<TimezoneModel[]>([]);
  const branches = useSelector(
    (state: RootState) => state.branch.branchList,
    shallowEqual
  );

  const separateProductEachBranch = useSelector(
    (state: RootState) => state.globalValue.separateProductEachBranch,
    shallowEqual
  );
  const [cloneMenuInfo, setCloneMenuInfo] = useState<{
    branchId?: number;
    isModal: boolean;
  }>({
    isModal: false,
  });

  const _handleCloneMenu = async () => {
    if (!cloneMenuInfo.branchId) {
      message.error("Please select location");
      return;
    }
    dispatch(setLoading(true));
    try {
      await copyMenuFromOtherBranch({
        fromBranchId: cloneMenuInfo.branchId,
        branchId: model.id,
      });
    } catch (error) {
      message.error(
        error?.response?.data?.error?.message ??
          "Lỗi hệ thống"
      );
    }
    dispatch(setLoading(false));
  };

  useEffect(() => {
    getCountryList().then((res) => {
      setCountries(res.data);
    });
    getTimeZones().then((res) => {
      setTimeZones(res.data);
    });
  }, []);

  return (
    <Row gutter={[16, 16]}>
      <Col span="12">
        <Card
          title={t(CoreResource, Information)}
          extra={
            <Button
              hidden={!separateProductEachBranch || !isUpdateMode}
              onClick={() => {
                setCloneMenuInfo({ ...cloneMenuInfo, isModal: true });
              }}
              type="primary"
              icon={<CopyOutlined />}
            >
              Clone menu from other location
            </Button>
          }
        >
          <Input.Group className="mb-4">
            <Typography.Text> {"Tên"}: </Typography.Text>
            <Input
              value={model.name}
              onChange={(e) => {
                onChange({
                  ...model,
                  name: e.target.value,
                  seoFields: isUpdateMode
                    ? model.seoFields
                    : ({
                        ...model.seoFields,
                        metaTitle: e.target.value,
                      } as SeoModel),
                });
              }}
              placeholder={"Tên"}
            ></Input>
          </Input.Group>
          <Input.Group className="mb-4">
            <Typography.Text> Phone number: </Typography.Text>
            <Input
              value={model.phone}
              onChange={(e) => {
                onChange({
                  ...model,
                  phone: e.target.value,
                });
              }}
              placeholder="Phone number"
            ></Input>
          </Input.Group>
          <Input.Group className="mb-4">
            <Typography.Text> Email: </Typography.Text>
            <Input
              value={model.email}
              onChange={(e) => {
                onChange({
                  ...model,
                  email: e.target.value,
                });
              }}
              placeholder="Email"
            ></Input>
          </Input.Group>
          <Input.Group className="mb-4">
            <Typography.Text>Tax Rate: </Typography.Text>
            <InputNumber
             className="w-100"
              value={model.taxRate}
              onChange={(e) => {
                onChange({
                  ...model,
                  taxRate: e,
                });
              }}
              placeholder="Tax Rate"
            ></InputNumber>
          </Input.Group>
          <Input.Group className="mb-4">
            <Typography.Text>Country:</Typography.Text>
            <Select
              className="w-100"
              placeholder="Select country"
              showSearch
              filterOption={(input, option: any) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              value={model.address.countryCode}
              onChange={(value) => {
                const country = countries.find((m) => m.countryCode === value);
                if (country) {
                  model.address.countryCode = country.countryCode;
                  model.address.countryName = country.name;
                  model.address.stateCode = undefined;
                  model.address.stateName = undefined;
                  setStates(country.states);
                  onChange({ ...model });
                }
              }}
            >
              {countries.map((country) => (
                <Select.Option
                  key={country.countryCode}
                  value={country.countryCode}
                >
                  {country.name}
                </Select.Option>
              ))}
            </Select>
          </Input.Group>
          <Input.Group className="mb-4">
            <Typography.Text>State:</Typography.Text>
            <Select
              className="w-100"
              placeholder="Select state"
              showSearch
              filterOption={(input, option: any) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              value={model.address.stateCode}
              onChange={(value) => {
                const state = states.find((m) => m.stateCode === value);
                if (state) {
                  model.address.stateCode = state.stateCode;
                  model.address.stateName = state.name;
                  onChange({ ...model });
                }
              }}
            >
              {states.map((state) => (
                <Select.Option key={state.stateCode} value={state.stateCode}>
                  {state.name}
                </Select.Option>
              ))}
            </Select>
          </Input.Group>
          <Input.Group className="mb-4">
            <Typography.Text>City: </Typography.Text>
            <Input
              value={model.address.provinceName}
              onChange={(e) => {
                model.address.provinceName = e.target.value;
                onChange({
                  ...model,
                });
              }}
              placeholder="City"
            ></Input>
          </Input.Group>
          <Input.Group className="mb-4">
            <Typography.Text>Address: </Typography.Text>
            <Input
              value={model.address.street}
              onChange={(e) => {
                model.address.street = e.target.value;
                onChange({
                  ...model,
                });
              }}
              placeholder="Address"
            ></Input>
          </Input.Group>
          <Input.Group className="mb-4">
            <Typography.Text>Postal code: </Typography.Text>
            <Input
              value={model.address.postalCode}
              onChange={(e) => {
                model.address.postalCode = e.target.value;
                onChange({
                  ...model,
                });
              }}
              placeholder="Postal Code"
            ></Input>
          </Input.Group>
          <Input.Group className="mb-4">
            <Typography.Text>Default Printer IP: </Typography.Text>
            <Input
              value={model.defaultPrinterIp}
              onChange={(e) => {
                model.defaultPrinterIp = e.target.value;
                onChange({
                  ...model,
                });
              }}
              placeholder="Default Printer IP"
            ></Input>
          </Input.Group>
          <Input.Group className="mb-4">
            <Typography.Text>Sort:  </Typography.Text>
            <InputNumber
              className="w-100"
              value={model.sort}
              onChange={(value) => {
                model.sort = value;
                onChange({
                  ...model,
                });
              }}
              placeholder="Sort"
            ></InputNumber>
          </Input.Group>
          <Input.Group className="mb-4">
            <Typography.Text>Warehouses: </Typography.Text>
            <Select
              className="w-100"
              placeholder="Select warehouses"
              value={model.wareHouses?.map((m) => m.id) ?? []}
              onChange={(e) => {
                if (!e) {
                  onChange({ ...model, wareHouses: [] });
                  return;
                }
                model.wareHouses = e.map((m) => {
                  const wareHouse = wareHouses.find((w) => w.id === m);
                  return wareHouse as WareHouseModel;
                });
                onChange({ ...model });
              }}
              showSearch
              filterOption={(input, option: any) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              mode="multiple"
              allowClear
            >
              {wareHouses.map((wareHouse) => (
                <Select.Option value={wareHouse.id}>
                  {wareHouse.name}
                </Select.Option>
              ))}
            </Select>
          </Input.Group>
          <Input.Group className="mb-4">
            <Typography.Text>Timezone:</Typography.Text>
            <Select
              className="w-100"
              placeholder="Select timezone"
              showSearch
              filterOption={(input, option: any) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              value={model.timeZone}
              onChange={(value) => {
                model.timeZone = value;
                onChange({ ...model });
              }}
            >
              {timeZones.map((timeZone) => (
                <Select.Option key={timeZone.id} value={timeZone.id}>
                  {timeZone.displayName}
                </Select.Option>
              ))}
            </Select>
          </Input.Group>
          <Input.Group className="mb-4">
            <Checkbox
              checked={model.isActive}
              onChange={(e) => {
                onChange({ ...model, isActive: e.target.checked });
              }}
            >
              {t(CoreResource, IsActive)}
            </Checkbox>
          </Input.Group>
          <Input.Group className="mb-4">
            <Checkbox
              checked={model.isUpComing}
              onChange={(e) => {
                onChange({ ...model, isUpComing: e.target.checked });
              }}
            >
              Is UpComing
            </Checkbox>
          </Input.Group>
        </Card>
      </Col>
      <Col span="12"></Col>
      <Modal
        title="Clone menu info"
        visible={cloneMenuInfo.isModal}
        okText="Clone"
        onCancel={() => {
          setCloneMenuInfo({ ...cloneMenuInfo, isModal: false });
        }}
        onOk={_handleCloneMenu}
      >
        <Select
          className="w-100"
          placeholder="Select location"
          value={cloneMenuInfo?.branchId}
          onChange={(e) => {
            setCloneMenuInfo({ ...cloneMenuInfo, branchId: e });
          }}
          showSearch
          filterOption={(input, option: any) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {branches.map((branch) => (
            <Select.Option value={branch.id}>{branch.name}</Select.Option>
          ))}
        </Select>
      </Modal>
    </Row>
  );
}
